/**
 * Performs a deep comparison between two values to determine if they are equivalent.
 *
 * @function isEqual
 * @memberof is-equal
 *
 * @param {*} a - The first value to compare.
 * @param {*} b - The second value to compare.
 *
 * @returns {boolean} Returns true if the values are equivalent, else false.
 */

const cached = new WeakMap();
const isEqual = (a, b) => {
    if (a === null || b === null) {
        return a === b;
    }

    if (typeof a !== 'object' || typeof b !== 'object') {
        return a === b;
    }

    const dataTypeA = detectDataType(a);
    const dataTypeB = detectDataType(b);
    if (dataTypeA !== dataTypeB) {
        return false;
    }

    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length !== keysB.length) {
        return false;
    }

    const symbolsA = Object.getOwnPropertySymbols(a);
    const symbolsB = Object.getOwnPropertySymbols(b);
    if (symbolsA.length !== symbolsB.length) {
        return false;
    }

    if (cached.get(a)?.has(b)) {
        return true;
    }
    if (cached.get(b)?.has(a)) {
        return true;
    }

    cache(a, b, cached);

    const propertyNamesA = [...keysA, ...symbolsA];

    for (const propertyNameA of propertyNamesA) {
        if (!b.hasOwnProperty(propertyNameA)) {
            return false;
        }

        const propertyValueA = a[propertyNameA];
        const propertyValueB = b[propertyNameA];

        if (!isEqual(propertyValueA, propertyValueB)) {
            return false;
        }
    }

    return true;
};

/**
 * Caches associations between two values in a Map of Sets.
 * Given two values 'a' and 'b', it ensures that 'a' is associated with 'b'
 * and 'b' is associated with 'a' in the provided 'cached' Map.
 *
 * @function cache
 *
 * @param {*} a - The first value to cache.
 * @param {*} b - The second value to cache.
 * @param {Map} cached - The Map used for caching associations.
 */
function cache(a, b, cached) {
    const associate = value => {
        let variableToSet = cached.get(value);
        if (!variableToSet) {
            variableToSet = new Set();
            cached.set(value, variableToSet);
        }
        variableToSet.add(value);
    };
    associate(a);
    associate(b);
}

/**
 * Detects the data type of the input value.
 * If the value is an array, it returns 'array'.
 * Otherwise, it assumes the value is an object and returns 'object'.
 *
 * @function detectDataType
 *
 * @param {*} value - The input value to determine the data type.
 *
 * @returns {string} The detected data type ('array' or 'object').
 * @memberof Mojito.Utils
 */
function detectDataType(value) {
    if (Array.isArray(value)) {
        return 'array';
    }
    return 'object';
}

export default isEqual;
